import { useState } from 'react';
import { INBOX_LANE_ADDRESSES } from '../../../shared/inbox-address';
import './InboxAddress.css';

interface InboxAddressProps {
  /** The couple's 6-char code (server-issued). Present = the couple is
   *  paired. The addresses themselves carry no code (card QLPMbGu3EiXm), so
   *  this is only the gate for whether there is an inbox to address. */
  code?: string | null;
  /** A stable id suffix so a walk can target each rendered address + copy btn. */
  idPrefix: string;
  /** The one-line explanation of what lands in this inbox. Omit for the bare row. */
  note?: string;
  /** 'row' = a Settings-style set-row; 'inline' = a compact line under a lede. */
  variant?: 'row' | 'inline';
}

/**
 * The couple's two inbox addresses (card QLPMbGu3EiXm) — `us@` shared by both,
 * `me@` private to whoever sent it. The SAME two for every couple, so there is
 * no code to copy, mistype or remember: which couple a message belongs to is
 * resolved from its sender, not its recipient.
 *
 * Every surface (Admin menu, Settings, invite moment) renders THIS component
 * from the one constant, so the two addresses can never drift between menus.
 * A null code renders nothing — no couple, no inbox to address yet.
 *
 * Id contract: the SHARED row keeps the original `-value` / `-copy` ids that
 * `scripts/walk-inbox-address.mjs` has always targeted, so the primary
 * address stays the primary assertion. The private row is suffixed `-me`.
 */
export function InboxAddress({ code = null, idPrefix, note, variant = 'inline' }: InboxAddressProps) {
  const [copied, setCopied] = useState<'me' | 'us' | null>(null);
  if (!code) return null;

  const copy = async (lane: 'me' | 'us') => {
    try {
      await navigator.clipboard.writeText(INBOX_LANE_ADDRESSES[lane]);
      setCopied(lane);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {
      // Clipboard blocked (insecure context / permissions) — the address is
      // still selectable on screen, so a failed copy is not a dead end.
    }
  };

  return (
    <div className={`pear-inbox-addr pear-inbox-addr--${variant}`} id={`${idPrefix}-inbox-addr`}>
      <div className="pear-inbox-addr-text">
        <span className="pear-inbox-addr-label" id={`${idPrefix}-inbox-addr-label`}>
          Your inbox addresses
        </span>

        <div className="pear-inbox-addr-row">
          <span className="pear-inbox-addr-kind">Shared</span>
          <code className="pear-inbox-addr-value" id={`${idPrefix}-inbox-addr-value`}>
            {INBOX_LANE_ADDRESSES.us}
          </code>
          <button
            type="button"
            className={`pear-inbox-addr-copy${copied === 'us' ? ' is-copied' : ''}`}
            id={`${idPrefix}-inbox-addr-copy`}
            aria-label="Copy the shared inbox address"
            onClick={() => void copy('us')}
          >
            {copied === 'us' ? 'Copied' : 'Copy'}
          </button>
        </div>

        <div className="pear-inbox-addr-row">
          <span className="pear-inbox-addr-kind">Private</span>
          <code className="pear-inbox-addr-value" id={`${idPrefix}-inbox-addr-me-value`}>
            {INBOX_LANE_ADDRESSES.me}
          </code>
          <button
            type="button"
            className={`pear-inbox-addr-copy${copied === 'me' ? ' is-copied' : ''}`}
            id={`${idPrefix}-inbox-addr-me-copy`}
            aria-label="Copy the private inbox address"
            onClick={() => void copy('me')}
          >
            {copied === 'me' ? 'Copied' : 'Copy'}
          </button>
        </div>

        {note && (
          <p className="pear-inbox-addr-note" id={`${idPrefix}-inbox-addr-note`}>
            {note}
          </p>
        )}
      </div>
    </div>
  );
}
